import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, LoginResult, SelectCustomerResult } from './AuthContext';
import { authService } from '@/services/authService';
import { User, Customer, RecordingStatus } from './types';
import { useToast } from '@/components/ui/use-toast';

/**
 * Authentication Provider Component
 * 
 * @description
 * This component provides authentication state and functionality to the entire application.
 * It handles:
 * - User authentication (login/logout)
 * - Session initialization and persistence
 * - Customer selection and management
 * - Session recording capabilities
 * 
 * The AuthProvider wraps the application and exposes authentication functionality
 * through the AuthContext, which can be accessed using the useAuth hook.
 * 
 * @example
 * ```tsx
 * // In your app's root component
 * import { AuthProvider } from '@/contexts/auth';
 * 
 * const App = () => (
 *   <AuthProvider>
 *     <YourApp />
 *   </AuthProvider>
 * );
 * ```
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ 
  children 
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // Auth state
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // User data
  const [user, setUser] = useState<User | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [activeCustomer, setActiveCustomer] = useState<Customer | null>(null);

  // Recording status
  const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>({
    isRecording: false,
    isPaused: false,
  });

  /**
   * Initialize authentication state on application load
   * 
   * @description
   * Checks for existing session, restores user and customer state if available,
   * and sets up the initial authentication state of the application.
   */
  const initialize = useCallback(async () => {
    setIsLoading(true);
    
    try {
      // Check if user is logged in
      const session = await authService.checkSession();
      
      if (session.isValid) {
        // Set authenticated state
        setIsAuthenticated(true);
        setUser(session.user);
        
        // Only load customers if we don't have them cached or if active customer is missing
        if (!session.activeCustomer || customers.length === 0) {
          const customersList = await authService.getCustomers();
          setCustomers(customersList);
          
          // Check for active customer
          if (session.activeCustomer) {
            setActiveCustomer(session.activeCustomer);
          } else if (session.user?.last_active_customer && customersList.length > 0) {
            // Try to restore last active customer
            const lastCustomer = customersList.find(c => 
              c.id === session.user?.last_active_customer
            );
            
            if (lastCustomer) {
              await authService.selectCustomer(lastCustomer);
              setActiveCustomer(lastCustomer);
            }
          }
        } else {
          // Use cached customers if available
          setCustomers(customers);
          setActiveCustomer(session.activeCustomer);
        }

        // Check if session recording is active (only if needed)
        const recordingState = await authService.getRecordingStatus();
        setRecordingStatus(recordingState);
      }
    } catch (error) {
      console.error("Auth initialization error:", error);
      // Clear any potentially corrupted auth state
      authService.clearSession();
    } finally {
      setIsInitialized(true);
      setIsLoading(false);
    }
  }, [customers.length]);

  // Initialize on mount
  useEffect(() => {
    initialize();
  }, [initialize]);

  /**
   * Handles user login
   * 
   * @description
   * Authenticates a user with email and password, loads their available customers,
   * and optionally starts session recording. On success, it attempts to restore
   * the last active customer or guides the user to select one.
   * 
   * @param email - User's email address
   * @param password - User's password
   * @param recordSession - Whether to start session recording after login
   * @param redirectTo - Optional override for redirect destination (defaults to dashboard)
   * @returns Promise with login result and redirect information
   */
  const login = async (
    email: string, 
    password: string, 
    recordSession = false,
    redirectTo?: string
  ): Promise<LoginResult> => {
    try {
      setIsLoading(true);
      const result = await authService.login(email, password);
      
      if (result.success) {
        // Set auth state
        setIsAuthenticated(true);
        setUser(result.user);
        
        // Load customers
        const customersList = await authService.getCustomers();
        setCustomers(customersList);
        
        // Start recording if requested
        if (recordSession) {
          await authService.startRecording();
          setRecordingStatus({ isRecording: true, isPaused: false });
          
          toast({
            title: "Session Recording Active",
            description: "Your session is being recorded for support purposes.",
            duration: 3000,
          });
        }
        
        // Broadcast login event to other tabs
        localStorage.setItem('login-event', Date.now().toString());
        
        // Welcome message
        toast({
          title: `Welcome ${result.user.first_name || email}!`,
          description: "You have successfully logged in.",
          duration: 3000,
        });
        
        // Determine redirect based on customer status
        let finalRedirectTo = redirectTo || '/dashboard'; // Use provided redirect or default to dashboard
        
        // Process customer selection logic
        if (result.user.last_active_customer && customersList.length > 0) {
          // User has a last active customer, try to find and activate it
          const lastCustomer = customersList.find(c => c.id === result.user.last_active_customer);
          
          if (lastCustomer) {
            // Last active customer found, activate it
            await authService.selectCustomer(lastCustomer);
            setActiveCustomer(lastCustomer);
            console.log(`Last active customer ${lastCustomer.name} found and activated`);
            // Use the provided redirectTo since customer is now selected
          } else if (customersList.length > 1) {
            // Last active customer not found but user has multiple customers
            finalRedirectTo = '/select-customer'; // Need to select a customer
            console.log('Last active customer not found, multiple customers available');
          } else {
            // Last active customer not found but only one customer available
            await authService.selectCustomer(customersList[0]);
            setActiveCustomer(customersList[0]);
            console.log(`Only one customer available, selected ${customersList[0].name}`);
            // Use the provided redirectTo since customer is now selected
          }
        } else if (customersList.length > 1) {
          // No last active customer and multiple customers available
          finalRedirectTo = '/select-customer';
          console.log('No last active customer, multiple customers available');
        } else if (customersList.length === 1) {
          // No last active customer but only one available
          await authService.selectCustomer(customersList[0]);
          setActiveCustomer(customersList[0]);
          console.log(`No last active customer, selected only available customer: ${customersList[0].name}`);
          // Use the provided redirectTo since customer is now selected
        } else {
          // No customers available - unusual case
          toast({
            title: "No Customer Access",
            description: "Your account doesn't have access to any customers.",
            variant: "destructive",
            duration: 5000,
          });
          console.warn('User has no customer access');
        }
        
        console.log(`Login successful, redirecting to: ${finalRedirectTo}`);
        return { success: true, redirectTo: finalRedirectTo };
      }
      
      return { 
        success: false, 
        error: result.error || "Invalid credentials. Please try again." 
      };
    } catch (error) {
      console.error("Login error:", error);
      return { 
        success: false, 
        error: "An unexpected error occurred. Please try again." 
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles user logout
   * 
   * @description
   * Logs out the current user, stops any active recording sessions,
   * clears authentication state, and redirects to the login page.
   */
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      // Stop recording if active
      if (recordingStatus.isRecording) {
        await authService.stopRecording();
      }
      
      // Perform logout
      await authService.logout();
      
      // Clear auth state
      setIsAuthenticated(false);
      setUser(null);
      setCustomers([]);
      setActiveCustomer(null);
      setRecordingStatus({ isRecording: false, isPaused: false });
      
      // Redirect to login
      navigate('/login', { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      
      // Force logout even on error
      authService.clearSession();
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handles customer selection
   * 
   * @description
   * Updates the active customer for the current user session,
   * updates user preferences, and persists the selection.
   * Also clears React Query cache and resets site selection
   * to ensure proper context switching between customers.
   * 
   * @param customer - The customer to select
   * @returns Promise with selection result
   */
  const selectCustomer = async (customer: Customer): Promise<SelectCustomerResult> => {
    try {
      setIsLoading(true);
      
      // Get QueryClient instance
      const queryClient = await import('@/lib/queryClient').then(module => module.queryClient);
      
      const result = await authService.selectCustomer(customer);
      
      if (result.success) {
        // Set active customer in state
        setActiveCustomer(customer);
        
        // Clear the React Query cache to prevent stale data from previous customer
        queryClient.clear();
        
        // Update user with new last_active_customer
        if (user) {
          setUser({
            ...user,
            last_active_customer: customer.id,
          });
        }
        
        // Remove the active site from localStorage to trigger auto-selection
        localStorage.removeItem('active_site');
        
        // Invalidate site queries to force a refresh
        queryClient.invalidateQueries({ queryKey: ['sites'] });
        
        // Dispatch an event to notify components about customer change
        window.dispatchEvent(new CustomEvent('customer-changed', {
          detail: { customerId: customer.id }
        }));
        
        return { success: true };
      }
      
      return {
        success: false,
        error: result.error || "Failed to switch customer. Please try again."
      };
    } catch (error) {
      console.error("Select customer error:", error);
      return {
        success: false,
        error: "An unexpected error occurred. Please try again."
      };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Starts a new session recording
   * 
   * @description
   * Begins recording the user's session for support and debugging purposes.
   * Updates recording status and displays a notification.
   */
  const startRecording = useCallback(() => {
    authService.startRecording().then(() => {
      setRecordingStatus({ isRecording: true, isPaused: false });
      
      toast({
        title: "Session Recording Started",
        description: "Your session is now being recorded.",
        duration: 3000,
      });
    }).catch(error => {
      console.error("Error starting recording:", error);
    });
  }, [toast]);

  /**
   * Pauses the current session recording
   * 
   * @description
   * Temporarily pauses an active session recording.
   * Updates recording status and displays a notification.
   */
  const pauseRecording = useCallback(() => {
    if (!recordingStatus.isRecording || recordingStatus.isPaused) return;
    
    authService.pauseRecording().then(() => {
      setRecordingStatus({ isRecording: true, isPaused: true });
      
      toast({
        title: "Session Recording Paused",
        description: "Your session recording has been paused.",
        duration: 3000,
      });
    }).catch(error => {
      console.error("Error pausing recording:", error);
    });
  }, [recordingStatus, toast]);

  /**
   * Resumes a paused session recording
   * 
   * @description
   * Resumes a previously paused session recording.
   * Updates recording status and displays a notification.
   */
  const resumeRecording = useCallback(() => {
    if (!recordingStatus.isRecording || !recordingStatus.isPaused) return;
    
    authService.resumeRecording().then(() => {
      setRecordingStatus({ isRecording: true, isPaused: false });
      
      toast({
        title: "Session Recording Resumed",
        description: "Your session recording has resumed.",
        duration: 3000,
      });
    }).catch(error => {
      console.error("Error resuming recording:", error);
    });
  }, [recordingStatus, toast]);

  /**
   * Stops the current session recording
   * 
   * @description
   * Ends the current session recording and finalizes the recording.
   * Updates recording status and displays a notification.
   */
  const stopRecording = useCallback(() => {
    if (!recordingStatus.isRecording) return;
    
    authService.stopRecording().then(() => {
      setRecordingStatus({ isRecording: false, isPaused: false });
      
      toast({
        title: "Session Recording Stopped",
        description: "Your session recording has been stopped and saved.",
        duration: 3000,
      });
    }).catch(error => {
      console.error("Error stopping recording:", error);
    });
  }, [recordingStatus, toast]);

  useEffect(() => {
    const onForceLogout = () => {
      logout();
    };
    window.addEventListener('force-logout', onForceLogout);
    return () => window.removeEventListener('force-logout', onForceLogout);
  }, [logout]);

  // Context value
  const contextValue = {
    isAuthenticated,
    isInitialized,
    isLoading,
    isRefreshing,
    user,
    customers,
    activeCustomer,
    recordingStatus,
    login,
    logout,
    selectCustomer,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
